import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { borderPrimary, canvasBg, cardBg, mutedText, primaryText, secondaryText } from '../../../../styling/stylingHelper';
import { REPORT_TYPE_OPTIONS, ReportTypeOption } from './reportTypes';
import ReportDownloadSlideOver from './ReportDownloadSlideOver';

interface Props {
  /** null = all-restaurants mode; reports are per restaurant, so the grid asks to pick one. */
  restaurantId: string | null;
}

/** Reports block of /analysis: one card per emailable report type → slide-over with picker + download. */
export default function AnalysisReportsSection({ restaurantId }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<ReportTypeOption | null>(null);

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className={cn('text-lg sm:text-xl font-semibold', primaryText)}>{t('analysisReports.title')}</h2>
        <p className={cn('text-sm', mutedText)}>{t('analysisReports.subtitle')}</p>
      </div>

      {!restaurantId ? (
        <div className={cn('rounded-xl border px-4 py-6 text-center text-sm', borderPrimary, cardBg, mutedText)}>
          {t('analysisReports.pickRestaurant')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {REPORT_TYPE_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.type}
                type="button"
                onClick={() => setSelected(option)}
                className={cn(
                  'group flex items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                  borderPrimary,
                  cardBg,
                  'hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark'
                )}
              >
                <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', canvasBg, secondaryText)}>
                  <Icon className="w-5 h-5" />
                </span>
                <span className="flex flex-col min-w-0 flex-1">
                  <span className={cn('text-sm sm:text-base font-semibold break-words', primaryText)}>
                    {t(`analysisReports.types.${option.type}.title`)}
                  </span>
                  <span className={cn('text-xs sm:text-sm break-words', mutedText)}>
                    {t(`analysisReports.types.${option.type}.description`)}
                  </span>
                </span>
                <Download className={cn('w-4 h-4 mt-1 shrink-0', mutedText)} />
              </button>
            );
          })}
        </div>
      )}

      {restaurantId && selected && (
        <ReportDownloadSlideOver
          key={`${restaurantId}-${selected.type}`}
          restaurantId={restaurantId}
          option={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}
